/**
 * ToastHost — stacked top-right toast region.
 *
 * Mounted once in AppLayout. Consumes `useToastStore`. Each toast carries
 * a kind-coloured left border accent:
 *
 *   info     → blue
 *   success  → emerald
 *   error    → red
 *
 * Max 3 visible (configured in toastStore). Older toasts auto-dismiss
 * after their kind-specific timeout; the user can also dismiss manually.
 */

import { cx } from './cx';
import { useToastStore, MAX_VISIBLE, type Toast } from './toastStore';

const ACCENT: Record<Toast['kind'], string> = {
  info: 'border-l-blue-500',
  success: 'border-l-emerald-500',
  error: 'border-l-red-500',
};

const ICON: Record<Toast['kind'], string> = {
  info: 'info',
  success: 'check_circle',
  error: 'error',
};

const ICON_COLOR: Record<Toast['kind'], string> = {
  info: 'text-blue-600',
  success: 'text-emerald-600',
  error: 'text-red-600',
};

export default function ToastHost() {
  const toasts = useToastStore((s) => s.toasts);
  const dismiss = useToastStore((s) => s.dismiss);

  // Render the last N. Older ones queue; as visible ones dismiss, the next
  // slide up automatically because we always slice the tail.
  const visible = toasts.slice(-MAX_VISIBLE);

  if (visible.length === 0) return null;

  return (
    <div
      role="region"
      aria-label="Notifications"
      aria-live="polite"
      className="fixed top-4 right-4 z-[70] flex flex-col gap-2 max-w-sm w-[calc(100%-2rem)] md:w-auto pointer-events-none"
    >
      {visible.map((t) => (
        <div
          key={t.id}
          className={cx(
            'bg-white border border-outline-variant rounded-lg shadow-pop p-3 flex items-start gap-3 pointer-events-auto border-l-4',
            ACCENT[t.kind],
          )}
        >
          <span
            aria-hidden="true"
            className={cx(
              'material-symbols-outlined text-[20px] shrink-0 mt-0.5',
              ICON_COLOR[t.kind],
            )}
          >
            {ICON[t.kind]}
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-secondary leading-snug">{t.message}</p>
            {t.action && (
              <button
                type="button"
                onClick={() => {
                  t.action?.onClick();
                  dismiss(t.id);
                }}
                className="text-[11px] font-bold uppercase tracking-wider text-primary mt-1.5"
              >
                {t.action.label}
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={() => dismiss(t.id)}
            className="text-outline hover:text-secondary shrink-0"
            aria-label="Dismiss notification"
          >
            <span className="material-symbols-outlined text-[18px]">close</span>
          </button>
        </div>
      ))}
    </div>
  );
}
